import React, {useState} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Skeleton,
    Typography,
    type SxProps,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useWpCheckSummaryRegistry, type WpCheckSummaryItem} from '@/pages/components/audit/adjustment/adjust/dialogs/basic/common/wpCheckSummaryContext';

export interface WpCheckAccordionProps {
    children: React.ReactNode;
    sx?: SxProps;
    /** When true, accordion starts open (default collapsed). */
    defaultExpanded?: boolean;
}

function BalanceChip({balanced, softUnbalanced}: {balanced: boolean; softUnbalanced?: boolean}) {
    // Soft unbalance: |control| in (0, 1] → warning (yellow); else error (red)
    const color = balanced ? 'success' : softUnbalanced ? 'warning' : 'error';
    return (
        <Chip
            label={balanced ? 'Balancing' : 'UnBalancing'}
            color={color}
            size="small"
        />
    );
}

function CheckSummarySegment({
    item,
    showName,
}: {
    item: WpCheckSummaryItem;
    showName: boolean;
}) {
    const hasYoa = Boolean(item.yoa);
    // Follow PerCheck-registered ignoreLastYear (MULTI FirstAudit: first tab only)
    const hasLastYoa = !item.ignoreLastYear && Boolean(item.lastYoa);

    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                flexWrap: 'nowrap',
                whiteSpace: 'nowrap',
            }}
        >
            {showName && (
                <Typography variant="body2" component="span" sx={{fontWeight: 600, whiteSpace: 'nowrap'}}>
                    {item.name}
                </Typography>
            )}
            {showName && (
                <Typography variant="body2" component="span">
                    (
                </Typography>
            )}
            {hasYoa ? (
                <>
                    <Typography variant="body2" component="span" sx={{whiteSpace: 'nowrap'}}>
                        {item.yoa}:
                    </Typography>
                    <BalanceChip
                        balanced={item.currentBalanced}
                        softUnbalanced={item.currentSoftUnbalanced}
                    />
                </>
            ) : (
                <BalanceChip
                    balanced={item.currentBalanced}
                    softUnbalanced={item.currentSoftUnbalanced}
                />
            )}
            {hasLastYoa && (
                <>
                    <Typography variant="body2" component="span" sx={{whiteSpace: 'nowrap'}}>
                        {item.lastYoa}:
                    </Typography>
                    <BalanceChip
                        balanced={item.lastYearBalanced}
                        softUnbalanced={item.lastYearSoftUnbalanced}
                    />
                </>
            )}
            {showName && (
                <Typography variant="body2" component="span">
                    )
                </Typography>
            )}
        </Box>
    );
}

function AccordionSummaryContent({
    summaries,
}: {
    summaries: WpCheckSummaryItem[];
}) {
    if (summaries.length === 0) {
        return <Skeleton variant="text" width={240} height={28}/>;
    }

    const showName = summaries.length > 1;

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
                whiteSpace: 'nowrap',
                gap: 2,
                py: 0.5,
                width: 'max-content',
            }}
        >
            {summaries.map((item, index) => (
                <CheckSummarySegment
                    key={`${item.name}-${index}`}
                    item={item}
                    showName={showName}
                />
            ))}
        </Box>
    );
}

export function WpCheckAccordion({children, sx, defaultExpanded = false}: WpCheckAccordionProps) {
    const ctx = useWpCheckSummaryRegistry();
    const summaries = ctx?.summaries ?? [];
    const [expanded, setExpanded] = useState(defaultExpanded);

    const handleChange = (_event: React.SyntheticEvent, nextExpanded: boolean) => {
        setExpanded(nextExpanded);
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={handleChange}
            disableGutters
            elevation={0}
            sx={{
                width: '100%',
                minWidth: 'min-content',
                border: 'none',
                boxShadow: 'none',
                backgroundColor: 'transparent',
                '&:before': {display: 'none'},
                ...sx,
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon/>}
                sx={{
                    minHeight: 48,
                    px: 0,
                    overflow: 'visible',
                    '& .MuiAccordionSummary-content': {
                        my: 0.5,
                        margin: 0,
                        flex: '1 1 auto',
                        overflow: 'visible',
                        justifyContent: 'flex-end',
                        display: 'flex',
                        minWidth: 'min-content',
                    },
                }}
            >
                <AccordionSummaryContent summaries={summaries}/>
            </AccordionSummary>
            <AccordionDetails sx={{pt: 0, pb: 1, px: 0, overflow: 'visible'}}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-start',
                        gap: 2,
                        flexWrap: 'nowrap',
                        width: '100%',
                        '& > *': {
                            flex: '0 0 auto',
                        },
                    }}
                >
                    {children}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}
